import type { AxiosError } from "axios";

import { get, post } from "./api";
import { API } from "@/constants";

/**
 * Context Console — the self-serve context upload / evaluate / publish flow for
 * IQ Admins.
 *
 * Upload and evaluate are STATELESS on the backend: the page holds the five
 * picked files in memory and posts the whole set on every evaluation (including
 * a re-score after one file is fixed). Publish posts the same set again and is
 * the only call that writes — it upserts into `company_files` and stamps a new
 * live `context_sets` version.
 */

/** The five context inputs. Doubles as the multipart field name for each slot. */
export type ContextFileKey =
  | "business_logic"
  | "defaults"
  | "sql_templates"
  | "yamls"
  | "all_tables";

/** The five picked files, exactly as the page holds them. `yamls` is 1..N. */
export interface PickedContextFiles {
  business_logic: File | null;
  defaults: File | null;
  sql_templates: File | null;
  yamls: File[];
  all_tables: File | null;
}

/**
 * One finding from a scored file — what's wrong (or, on a clean file, what's
 * covered) and the concrete fix, as two separate PLAIN TEXT fields (markdown
 * backticks for identifiers, never HTML — the backend strips any markup
 * before this reaches the wire).
 *
 * A plain `string` is also accepted at the type level: a backend deployed
 * slightly behind or rolled back independently of this frontend can still
 * send the old single-sentence shape — see EvaluationPanel.tsx's render,
 * which handles both without throwing.
 */
export interface FileFinding {
  issue: string;
  fix: string;
}

/**
 * One scored row. `key` is the multipart slot it came from — the many uploaded
 * YAMLs collapse into a single `yml` row, so this is what the UI joins on rather
 * than the display name.
 *
 * `lead` is PLAIN TEXT with markdown backticks for identifiers, never HTML.
 * `body` is a list of separate findings — one per bullet, not one paragraph.
 */
export interface FileScore {
  key: Exclude<ContextFileKey, "yamls"> | "yml";
  file_name: string;
  score: number;
  /** Server-computed: `score >= pass_mark`. Never recomputed here. */
  pass: boolean;
  pass_mark: number;
  lead: string;
  body: (FileFinding | string)[];
  /** Real cost of scoring this file. Null only if the server's cost lookup failed. */
  cost_usd: number | null;
}

/** A context set's scoring result — from an evaluate, a live-set backfill, or `getLiveSetEvaluation`. */
export interface EvaluateResponse {
  /** Always five rows, in slot order. */
  files: FileScore[];
  combined_score: number;
  /** The gate, owned by the API — never hardcoded in the UI. */
  gate_threshold: number;
  gate_cleared: boolean;
  /**
   * Opaque hash of the scored bytes. NOT an authorization token — publish
   * re-enforces the gate server-side regardless of what's sent here.
   *
   * Currently inert on publish: the backend's `publish_context` handler
   * takes no `evaluation_token` field, so posting it (see `toFormData`)
   * is silently dropped. An unchanged set still avoids the five LLM calls
   * on publish, but via `_evaluate`'s own content-hash cache lookup, not
   * because this value was echoed back — verified against
   * app/api/context_console.py. Kept round-tripped for forward
   * compatibility in case the backend starts consuming it; remove this
   * note (and the round-trip, if still unused) if that never happens.
   */
  evaluation_token: string;
  /** Net cost of this scoring pass — sum of files[*].cost_usd. */
  total_cost_usd: number | null;
  /**
   * The live set this evaluation belongs to. Only ever populated by
   * `getLiveSetEvaluation` — every other caller of this shape (evaluate, the
   * live-set backfill) is scoring content that has no set yet.
   */
  set_id?: string;
}

export interface PublishedFile {
  /** The normalized storage key (e.g. `AllTables.yaml`). */
  file_name: string;
  /** What the admin actually uploaded — this is what the receipt shows. */
  original_filename: string;
  category: string;
  canonical_uri: string;
  current_revision?: number;
}

export interface PublishResponse {
  set_id: string;
  published_at: string;
  /** Null only for a skip-evaluation publish (`evaluation_pending: true`) —
   * every ordinary publish always scores first and always has one. */
  combined_score: number | null;
  /** False when this went live via the override — a below-threshold live
   * set. Null for a skip-evaluation publish — the gate was never evaluated. */
  gate_cleared: boolean | null;
  /** True only for a skip-evaluation publish, until `evaluateLiveSet()`
   * backfills a real score for this same set. */
  evaluation_pending: boolean;
  created: PublishedFile[];
  updated: PublishedFile[];
  unchanged: PublishedFile[];
  /** Net cost of the scoring pass this publish re-ran. Null for a
   * skip-evaluation publish, which makes no LLM calls. */
  total_cost_usd: number | null;
  /** Echoes what was submitted — also stored server-side for an audit trail. */
  publish_reason: string;
}

export interface LiveSet {
  id: string;
  published_at: string;
  combined_score: number | null;
  evaluation_pending: boolean;
}

/** One category's files as `GET /files` summarizes them — no content, just presence. */
export interface CompanyFileSummary {
  file_name: string;
  content_bytes: number;
  /** sha256 of the live content — lets the upload screen detect a same-name,
   * different-content re-pick without downloading the file first. */
  content_sha256: string;
  updated_at: string;
}

/** The backend's category names, keyed exactly as `company_files_store.COMPANY_CATEGORIES`. */
export type CompanyFileCategory =
  | "business_logic"
  | "defaults"
  | "sql_templates"
  | "AllTables"
  | "company_yamls";

/** `GET /files`. `files` backs the "already uploaded" check on evaluate; `live_set` is the header chip. */
export interface CompanyFilesResponse {
  live_set: LiveSet | null;
  files: Record<CompanyFileCategory, CompanyFileSummary[]>;
}

/** Bridges the backend's category names to this page's slot keys. */
const CATEGORY_TO_SLOT: Record<CompanyFileCategory, ContextFileKey> = {
  business_logic: "business_logic",
  defaults: "defaults",
  sql_templates: "sql_templates",
  AllTables: "all_tables",
  company_yamls: "yamls",
};

/** The inverse of {@link CATEGORY_TO_SLOT} — needed to call the download
 * endpoint, which takes the backend's category name, not the slot key. */
export const SLOT_TO_CATEGORY: Record<ContextFileKey, CompanyFileCategory> = {
  business_logic: "business_logic",
  defaults: "defaults",
  sql_templates: "sql_templates",
  all_tables: "AllTables",
  yamls: "company_yamls",
};

/** Slots the company already has a live file for, per `GET /files`. */
export const existingSlots = (files: CompanyFilesResponse["files"]): Set<ContextFileKey> => {
  const slots = new Set<ContextFileKey>();
  (Object.keys(CATEGORY_TO_SLOT) as CompanyFileCategory[]).forEach((category) => {
    if (files?.[category]?.length) slots.add(CATEGORY_TO_SLOT[category]);
  });
  return slots;
};

/** Reshapes `GET /files`' category-keyed summaries into the upload screen's
 * own slot keys, so the upload stage can prefill each row without knowing
 * the backend's category names at all. */
export const liveFilesBySlot = (
  files: CompanyFilesResponse["files"],
): Record<ContextFileKey, CompanyFileSummary[]> => {
  const bySlot: Record<ContextFileKey, CompanyFileSummary[]> = {
    business_logic: [], defaults: [], sql_templates: [], yamls: [], all_tables: [],
  };
  (Object.keys(CATEGORY_TO_SLOT) as CompanyFileCategory[]).forEach((category) => {
    bySlot[CATEGORY_TO_SLOT[category]] = files?.[category] ?? [];
  });
  return bySlot;
};

/**
 * Most rows the validator accepts in one call. Enforced server-side too; checked
 * before sending so an oversized set gets a sentence the admin can act on rather
 * than a raw framework validation error.
 */
export const MAX_QUESTION_ROWS = 200;

/** One row of the admin's regression question set. */
export interface QuestionRow {
  question: string;
  /** Optional: validity is judged on the question alone. */
  expected_answer: string | null;
  /** Optional ground-truth query — when any row carries one, the run asks the
   * server to execute it against BigQuery rather than trusting `expected_answer`
   * as-is (see `startEvaluationRun`). */
  standard_sql: string | null;
}

export interface ValidatedQuestion {
  /** 0-based position in the REQUEST array. */
  index: number;
  /** Echoed verbatim, so the UI can assert it renders the row it sent. */
  question: string;
  ok: boolean;
  /** Always a string — `""` when `ok`. */
  reason: string;
}

export interface ValidateResponse {
  results: ValidatedQuestion[];
  ok_count: number;
  flagged_count: number;
  /** Net cost of this validate request's LLM calls. */
  total_cost_usd: number | null;
}

/** The EXISTING evaluation item shape — reused as-is, not reshaped. */
export interface EvaluationItemRequest {
  question: string;
  standard_sql?: string | null;
  actual_answer?: string | null;
}

export type JobStatus = "pending" | "running" | "completed" | "failed";

export type QuantitativeStatus = "PASS" | "FAIL" | "UNSUCCESSFUL";

/** Computed verdict + supporting detail for one question's evaluation. */
export interface EvaluationDetails {
  quantitative_status: QuantitativeStatus | null;
  abs_pct_error: number | null;
  actual_result_json: string | null;
  /** Why the item couldn't be scored (no ground truth, BQ execution failed, …). */
  eval_error: string | null;
}

/** One question's full run result — IQ's answer plus its computed verdict. */
export interface EvaluationItemResponse {
  question: string;
  follow_up: boolean;
  /** A hard failure before any verdict could be computed (stream/HTTP error). */
  error_message: string | null;
  raw_answer: string;
  actual_answer: string | null;
  evaluation: EvaluationDetails | null;
  /** The chat's public uuid — same id every other chat-bearing API response
   * exposes, and what the app's /chat/:id route actually resolves against
   * (NOT the internal int PK). Links straight to that chat so an admin can
   * see the exact conversation IQ ran, not just the extracted answer. */
  chat_id: string | null;
}

/**
 * The run payload (backend `EvaluationResponse`). `results` carries every
 * question's full answer and verdict — the results screen's clickable
 * Correct/Incorrect/Unsuccessful lists are built from it.
 */
export interface EvaluationResponse {
  chat_id: string;
  results: EvaluationItemResponse[];
  total_questions: number;
  completed: number;
  passed: number;
  failed: number;
  errors: number;
  unsuccessful: number;
  context_set_id: string | null;
  company_name?: string;
  clickup_doc_link?: string | null;
  ragas_summary?: unknown;
}

/**
 * The job envelope. `error` and `result` are always-present keys holding null
 * while the job is pending/running — not optional keys.
 */
export interface EvaluationJobResponse {
  job_id: string;
  status: JobStatus;
  error: string | null;
  result: EvaluationResponse | null;
}

/**
 * Governed (eval-governance) accuracy run — app/evaluation/governance.py.
 *
 * Runs the question set once per provider tier: `primary` is every agent on
 * its currently configured model, `fallback` is every agent forced onto its
 * next model in line. Per the .agents/skills/eval-governance/SKILL.md
 * contract this backs: task success (`response.passed`/`.failed`), factual
 * correctness (`response.ragas_summary.faithfulness`), schema validity and
 * clarification rate (`metrics`), and behavior consistency across providers
 * (`cross_provider_consistency`).
 */
export type ProviderTier = "primary" | "fallback";

export interface TierMetrics {
  schema_validity_rate: number;
  clarification_rate: number;
  unsupported_assumption_count: number;
  input_tokens: number | null;
  output_tokens: number | null;
  cache_read_tokens: number | null;
  cache_creation_tokens: number | null;
  total_cost_usd: number | null;
  avg_latency_ms: number | null;
}

export interface TierResult {
  tier: ProviderTier;
  /** agent_name -> model actually used for this tier's run. */
  models: Record<string, string>;
  response: EvaluationResponse;
  metrics: TierMetrics;
}

/**
 * Auto-computable half of an eval-governance decision-log row: the proposed
 * default and the measured evidence. `team_decision` / `rationale` are always
 * null — the skill treats those as human judgment a run can't supply.
 */
export interface DecisionLogEvidence {
  proposed_default: string | null;
  alternatives: string[];
  team_decision: string | null;
  rationale: string | null;
  evidence: {
    primary_pass_rate: number | null;
    fallback_pass_rate: number | null;
    cross_provider_consistency: number;
    primary_models: Record<string, string>;
    fallback_models: Record<string, string>;
  };
}

/** Test & Run answers each question once, on the primary tier — `fallback` is
 * null (no fallback-tier pass, no cross-provider comparison to report). */
export interface GovernedEvaluationResponse {
  context_set_id: string | null;
  primary: TierResult;
  fallback: TierResult | null;
  cross_provider_consistency: number | null;
  decision_log_evidence: DecisionLogEvidence;
}

/** How many questions each provider tier has finished so far. */
export interface JobProgress {
  primary_completed: number;
  fallback_completed: number;
  /** 0 means unknown — the UI falls back to an indeterminate spinner. */
  total: number;
}

export interface GovernedEvaluationJobResponse {
  job_id: string;
  status: JobStatus;
  error: string | null;
  result: GovernedEvaluationResponse | null;
  progress: JobProgress;
  /** When this job last changed status — null only for a job just queued
   * this same request. */
  updated_at: string | null;
}

/**
 * The shared axios instance defaults to `Content-Type: application/json`, and
 * axios' `transformRequest` JSON-serializes a FormData body whenever the header
 * says JSON — which silently drops every File. Declaring multipart here keeps
 * the body intact; axios then unsets the header in the browser so XHR writes the
 * real boundary.
 */
const MULTIPART = { headers: { "Content-Type": "multipart/form-data" } } as const;

/** Everything a publish sends beyond the picked files — see `publishContext`. */
export interface PublishContextOptions {
  reason: string;
  evaluationToken?: string;
  overrideGate?: boolean;
  skipEvaluation?: boolean;
}

// Evaluate sends no options at all, hence Partial.
const toFormData = (
  files: PickedContextFiles,
  { evaluationToken, overrideGate, skipEvaluation, reason }: Partial<PublishContextOptions> = {},
): FormData => {
  const formData = new FormData();
  if (files.business_logic) formData.append("business_logic", files.business_logic);
  if (files.defaults) formData.append("defaults", files.defaults);
  if (files.sql_templates) formData.append("sql_templates", files.sql_templates);
  // Repeated part, one per file — the backend takes `list[UploadFile]`.
  files.yamls.forEach((file) => formData.append("yamls", file));
  if (files.all_tables) formData.append("all_tables", files.all_tables);
  if (evaluationToken) formData.append("evaluation_token", evaluationToken);
  if (overrideGate) formData.append("override_gate", "true");
  if (skipEvaluation) formData.append("skip_evaluation", "true");
  if (reason !== undefined) formData.append("reason", reason);
  return formData;
};

/**
 * Score the set. Writes nothing; a gate-failing set is a 200, not an error.
 *
 * Any slot may be left unpicked — the backend fills it from the company's live
 * file for that category. A slot with neither a pick nor a live file 400s with
 * `{missing}` (see `missingFilesPayload`) before any scoring runs.
 *
 * Opted out of the global error toast for EVERY status, not just 400: the page
 * renders the failure inline (with a Retry), and FileEvaluationProvider raises
 * its own toast when the admin has navigated elsewhere. A generic "Server
 * error" toast on top of either is a duplicate.
 */
export const evaluateContext = (files: PickedContextFiles) =>
  post<EvaluateResponse>(API.CONTEXT.EVALUATE, toFormData(files), {
    ...MULTIPART,
    skipGlobalErrorHandler: true,
  });

/**
 * Publish the set. The gate is a warning, not a hard stop: a below-threshold
 * set 409s carrying the fresh scores UNLESS `overrideGate` is set, in which
 * case the backend republishes anyway and the receipt's `gate_cleared: false`
 * says so. A set with a genuinely missing slot comes back 400 with
 * `{missing}`; a present-but-junk `reason` (see `validatePublishReason`) 422s;
 * `skipEvaluation` outside local/dev/test 403s. All four are expected
 * branches of the flow rather than errors, hence the global-toast opt-out.
 *
 * @param files - The picked files, same shape `evaluateContext` takes.
 * @param options.reason - Required free-text reason for this update, echoed
 * back on `PublishResponse.publish_reason`.
 * @param options.evaluationToken - Ties this publish to the evaluate that
 * scored these exact bytes (CC-B1's gate check).
 * @param options.overrideGate - Publish anyway despite a below-threshold
 * gate (the admin already saw the 409 and confirmed).
 * @param options.skipEvaluation - CC-B2's dev-only bypass; publishes without
 * ever scoring the set.
 */
export const publishContext = (
  files: PickedContextFiles,
  options: PublishContextOptions,
) =>
  post<PublishResponse>(API.CONTEXT.PUBLISH, toFormData(files, options), {
    ...MULTIPART,
    skipGlobalErrorHandler: [409, 400, 403, 422],
  });

/**
 * Backfill a real score, in place, for a set published via `skipEvaluation`
 * — see `PublishResponse.evaluation_pending`. Same response shape as
 * `evaluateContext`; writes nothing except the live `ContextSet` row itself.
 */
export const evaluateLiveSet = () =>
  post<EvaluateResponse>(API.CONTEXT.LIVE_SET_EVALUATE, undefined, {
    // Every status opts out, like `evaluateContext`: 404 (no live set) and 409
    // (not pending, or already backfilled by a concurrent call) are expected
    // outcomes, and any other failure is shown inline with a Retry on the page
    // (or by FileEvaluationProvider's own toast when the admin has navigated
    // away) — a generic "Server error" toast on top would duplicate it.
    skipGlobalErrorHandler: true,
  });

/**
 * The live set's already-published evaluation, full per-file breakdown
 * included — lets Evaluate & publish show a real result outside the tab
 * session that ran the original evaluate (that result otherwise only lives
 * in that tab's in-memory state). 404 covers both "nothing published yet"
 * and "published via skipEvaluation, never backfilled" — there's no
 * evaluation on record either way, and the caller doesn't need to tell
 * them apart.
 */
export const getLiveSetEvaluation = () =>
  get<EvaluateResponse>(API.CONTEXT.LIVE_SET_EVALUATION, { skipGlobalErrorHandler: [404] });

/**
 * Pull the scores out of a gate-fail rejection.
 *
 * The 409 body is field-identical to a 200 `EvaluateResponse` — one schema
 * parses both. The backend returns it directly rather than raising, precisely
 * so the app-wide exception handler doesn't rewrap it into `{status_code,
 * message}` and lose the scores. The `detail` fallback covers a handler-raised
 * variant; it should never fire.
 *
 * Returns null for anything that is not a gate failure, so real errors keep
 * surfacing as errors instead of rendering as an empty score table.
 */
export const gateFailPayload = (error: unknown): EvaluateResponse | null => {
  const response = (error as AxiosError<EvaluateResponse & { detail?: EvaluateResponse }>)?.response;
  if (response?.status !== 409) return null;
  const body = response.data;
  const payload = body?.files ? body : body?.detail;
  return payload?.files ? payload : null;
};

/**
 * Pull the missing-slot list out of a 400 rejection from evaluate/publish.
 *
 * Same rationale as `gateFailPayload`: the backend returns this directly
 * rather than raising, so the app-wide handler doesn't rewrap it under
 * `message`. Returns null for anything that isn't this specific shape, so
 * real 400s (e.g. a malformed file) still surface as errors.
 */
export const missingFilesPayload = (error: unknown): ContextFileKey[] | null => {
  const response = (error as AxiosError<{ missing?: ContextFileKey[]; detail?: { missing?: ContextFileKey[] } }>)
    ?.response;
  if (response?.status !== 400) return null;
  const body = response.data;
  const missing = body?.missing ?? body?.detail?.missing;
  return missing?.length ? missing : null;
};

/**
 * Pull the backend's own error text out of an evaluate/publish rejection.
 *
 * Every `HTTPException(detail=...)` this router raises (empty file, oversized
 * file/total, wrong slot, duplicate filename, malformed JSON, non-UTF-8
 * content, a merged slot gone stale, a concurrent publish race) is rewritten
 * by main.py's app-wide handler into `{status_code, message}` on the wire —
 * these are deliberately written for an admin to read, not internal/raw
 * (unlike a genuine 500, which has no admin-actionable specifics to show).
 * Returns null for the two structured-payload shapes that already have their
 * own dedicated UI (`gateFailPayload`'s 409, `missingFilesPayload`'s 400) and
 * for anything without a string `message` (network failure, a raw 500) — the
 * caller falls back to a generic message in those cases.
 */
export const apiErrorMessage = (error: unknown): string | null => {
  const response = (error as AxiosError<{ message?: string }>)?.response;
  const message = response?.data?.message;
  return typeof message === "string" && message.length > 0 ? message : null;
};

/**
 * True for an evaluate/publish rejection the admin can only fix by picking a
 * different or corrected file — empty, oversized (400/413), wrong slot,
 * duplicate filename, malformed JSON, non-UTF-8 content. Retrying the exact
 * same request would fail again identically, so a Retry button next to one
 * of these is a dead end, not a real recovery option.
 *
 * False for a genuine transient failure (502 "Scoring the context set
 * failed", a dropped connection) where the same request might actually
 * succeed on a second attempt, and false for the missing-slots 400 (its own
 * dedicated shape/UI, never reaches the generic error render this gates).
 */
export const isFileValidationError = (error: unknown): boolean => {
  const status = (error as AxiosError)?.response?.status;
  return status === 400 || status === 413;
};

export const validateQuestions = (questions: QuestionRow[]) =>
  post<ValidateResponse>(API.CONTEXT.QUESTIONS_VALIDATE, {
    // Validity is judged on the question text alone — standard_sql is only
    // ever read by the run endpoint's ground-truth resolution, not here.
    // Stripped rather than posted-and-ignored so the payload actually
    // matches what this endpoint uses.
    questions: questions.map(({ question, expected_answer }) => ({ question, expected_answer })),
  });

/**
 * Kick off a governed accuracy run against a published set (primary tier
 * only, per eval-governance). `run_bigquery` turns on when any row carries a
 * `standard_sql` — the server then executes that query as ground truth,
 * instead of scoring against the file-provided `expected_answer` verbatim.
 */
export const startEvaluationRun = (questions: QuestionRow[], contextSetId: string) =>
  post<GovernedEvaluationJobResponse>(API.CONTEXT.QUESTIONS_RUN, {
    context_set_id: contextSetId,
    run_bigquery: questions.some((row) => !!row.standard_sql),
    items: questions.map<EvaluationItemRequest>((row) => ({
      question: row.question,
      actual_answer: row.expected_answer,
      standard_sql: row.standard_sql,
    })),
  });

/**
 * A stale job id (a run that predates a worker/DB reset) 404s here on every
 * poll tick, and a persistent 5xx/network failure is no different — either
 * way `useEvaluationRun`/`EvaluationJobProvider` gives up on it and turns the
 * dead end into its own clear "lost track of the run" toast (and clears the
 * stranded id), so the generic global-error toast would just be a second,
 * less useful notification stacked on top.
 */
export const getEvaluationStatus = (jobId: string) =>
  get<GovernedEvaluationJobResponse>(API.CONTEXT.QUESTIONS_RUN_STATUS(jobId), {
    skipGlobalErrorHandler: true,
  });

export const getCompanyFiles = () => get<CompanyFilesResponse>(API.COMPANY_FILES);

/** The company's last validated/run question set (CC-B3), as persisted —
 * `content` is the same JSON shape `parseQuestionSet` already produces from
 * a raw file, so it re-hydrates through the same path a fresh upload would. */
export interface LatestQuestionSet {
  file_name: string;
  content: string;
  updated_at: string;
}

/** 404 (nothing saved yet) is an expected, UI-handled outcome — the console
 * just starts with no persisted set, not an error to toast. */
export const getLatestQuestionSet = () =>
  get<LatestQuestionSet>(API.CONTEXT.QUESTIONS_LATEST, { skipGlobalErrorHandler: [404] });

/** The most recently completed accuracy run for a context set (CC-B4) — lets
 * a fresh session show run history instead of only sessionStorage. 404 (no
 * completed run yet) is expected, not an error. */
export const getLatestRunForSet = (contextSetId: string) =>
  get<GovernedEvaluationJobResponse>(API.CONTEXT.QUESTIONS_RUN_LATEST, {
    params: { context_set_id: contextSetId },
    skipGlobalErrorHandler: [404],
  });

/** The completed run right before `excludeJobId`, for this company — shown
 * alongside whatever run is currently on screen, for a before/after
 * comparison across a republish. `excludeJobId: null` (nothing on screen
 * yet, e.g. right after a republish before the admin re-runs the test)
 * omits the param entirely — the backend then returns the plain latest
 * completed run instead of nothing, so "last run's score" is always shown
 * once one exists. Not scoped to a context set: a freshly published one has
 * at most one run of its own, so that filter would defeat the point. 404
 * (no completed run at all) is expected, not an error. */
export const getPreviousRun = (excludeJobId: string | null) =>
  get<GovernedEvaluationJobResponse>(API.CONTEXT.QUESTIONS_RUN_PREVIOUS, {
    params: excludeJobId ? { exclude_job_id: excludeJobId } : {},
    skipGlobalErrorHandler: [404],
  });

/** Fetch one live company file's raw content, for the upload screen's
 * Download action on a not-yet-locally-replaced slot.
 *
 * `skipGlobalErrorHandler`: every caller already toasts locally on failure
 * (directly, or via a wrapping carry-forward/reconstruct helper's own
 * try/catch) — without this, a failure here would ALSO fire the app-wide
 * toast, doubling up. `Promise.all` over several YAMLs (carrying the rest
 * of the live set forward) could otherwise raise N global toasts plus the
 * one local one. */
export const downloadCompanyFile = (category: CompanyFileCategory, fileName: string) =>
  get<Blob>(API.COMPANY_FILES_DOWNLOAD, {
    params: { category, file_name: fileName },
    responseType: "blob",
    skipGlobalErrorHandler: true,
  });

/** Every live context file bundled as one ZIP (CC-B5). Same
 * skipGlobalErrorHandler reasoning as {@link downloadCompanyFile} — the
 * caller already toasts locally. */
export const exportLiveFiles = () =>
  get<Blob>(API.COMPANY_FILES_EXPORT, { responseType: "blob", skipGlobalErrorHandler: true });
